import {
    AssetDeleteRequest,
    AssetDetailsResponse,
    AssetResponse
} from "@switcherstudio/api-core-client";
import {
    AssetDetails,
    AssetDetailsProps
} from "components/entity-details/AssetDetails";
import {
    ActionsBarOptions,
    ComponentItem,
    SearchSortOptions
} from "components/generic-multiselect/types";
import { useGenericMultiSelect } from "components/generic-multiselect/useGenericMultiSelect";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import TrashIcon from "assets/icons/trash-sm.svg?react";
import { useSwitcherClientCore } from "hooks/useSwitcherClient";
import { NotificationType } from "store/notification/types";
import { addNotification } from "store/notification/slice";
import { AppDispatch } from "store/store";
import { useDispatch } from "react-redux";
import { setActiveModal } from "store/modal/slice";
import { Modals } from "store/modal/types";
import { AssetEditModal } from "components/modal/AssetEditModal";
import { DeleteAssetsModal } from "components/modal/DeleteAssetsModal";

export interface AssetsTabProps {
    assets: AssetResponse[];
    loading: boolean;
    variant?: "all-assets" | "raw-files";
    refetchAssets: () => void;
    allProductionGroups: string[];
    getAssetById: (id: string) => AssetResponse | undefined;
}

export const AssetsTab: React.FC<AssetsTabProps> = ({
    assets,
    loading,
    refetchAssets,
    variant,
    allProductionGroups,
    getAssetById
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const allLoaded = true; // TODO: replace with actual logic
    const emptyStateCopy = t("cloud-assets:no-assets");

    const { dispatchApiRequest: deleteAssetsById } = useSwitcherClientCore(
        (client) => client.userAssets_DeleteUserAssetsAsync_v2,
        {
            requestImmediately: false,
            onSuccess: () => {
                dispatch(
                    addNotification({
                        type: NotificationType.Success,
                        message: t("cloud-assets:delete-success")
                    })
                );
                refetchAssets();
            },
            onError: () => {
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: t("cloud-assets:delete-failure")
                    })
                );
            }
        }
    );

    const { dispatchApiRequest: saveAsset } = useSwitcherClientCore(
        (client) => client.userAssets_PutUserAssets_v2,
        {
            requestImmediately: false,
            onSuccess: () => {
                dispatch(
                    addNotification({
                        type: NotificationType.Success,
                        message: t("cloud-assets:save-success")
                    })
                );
                refetchAssets();
            },
            onError: () => {
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: t("cloud-assets:save-failure")
                    })
                );
            }
        }
    );

    const deleteAssets = useCallback(
        async (assetsToDelete: AssetResponse[]) => {
            const idsToDelete: AssetDeleteRequest[] = assetsToDelete
                .map((a) => a?.Details?.Id)
                .filter((id): id is string => !!id)
                .map((id) => ({ Id: id }));

            await deleteAssetsById([idsToDelete]);
        },
        [deleteAssetsById]
    );

    const launchDeleteModal = useCallback(
        async (selectedAssets: AssetResponse[]) => {
            const relatedRawFiles: AssetResponse[] = [];
            const relatedAssets: AssetResponse[] = [];
            selectedAssets.forEach((asset) => {
                if (
                    asset?.RequiresAssetIds &&
                    asset.RequiresAssetIds.length > 0
                ) {
                    for (const rawFileId of asset.RequiresAssetIds) {
                        if (
                            !relatedRawFiles.some(
                                (file) => file.Details.Id === rawFileId
                            )
                        ) {
                            const file = getAssetById(rawFileId);
                            if (file) relatedRawFiles.push(file);
                        }
                    }
                } else if (
                    asset?.RequiredByAssetIds &&
                    asset.RequiredByAssetIds.length > 0
                ) {
                    for (const assetId of asset.RequiredByAssetIds) {
                        if (
                            !relatedAssets.some((a) => a.Details.Id === assetId)
                        ) {
                            const relatedAsset = getAssetById(assetId);
                            if (relatedAsset) relatedAssets.push(relatedAsset);
                        }
                    }
                }
            });

            dispatch(
                setActiveModal({
                    id: Modals.DeleteAssetsModal,
                    type: Modals.DeleteAssetsModal,
                    component: (
                        <DeleteAssetsModal
                            selectedAssets={selectedAssets}
                            relatedRawFiles={relatedRawFiles}
                            relatedAssets={relatedAssets}
                            deleteAssets={deleteAssets}
                            sourceTab={variant}
                        />
                    )
                })
            );
        },
        [dispatch, getAssetById, deleteAssets, variant]
    );

    const handleMultiDelete = useCallback(
        async (selected: Array<{ asset: AssetResponse }>) => {
            const assests = selected.map((s) => s.asset);
            await launchDeleteModal(assests);
        },
        [launchDeleteModal]
    );

    const handleEdit = useCallback(
        (asset: AssetResponse) => {
            dispatch(
                setActiveModal({
                    id: Modals.AssetEditModal,
                    type: Modals.AssetEditModal,
                    component: (
                        <AssetEditModal
                            asset={asset}
                            sourceTab={variant}
                            allProductionGroups={allProductionGroups}
                            getAssetById={getAssetById}
                            saveAsset={saveAsset}
                            refetchAssets={refetchAssets}
                        />
                    )
                })
            );
        },
        [
            dispatch,
            variant,
            allProductionGroups,
            getAssetById,
            saveAsset,
            refetchAssets
        ]
    );

    const assetListItemComponentItems: ComponentItem<AssetDetailsResponse>[] =
        useMemo(() => {
            if (!assets?.length) return [];
            return assets?.map((asset) => ({
                id: asset?.Details?.Id ?? "",
                component: (
                    <AssetDetails
                        key={asset?.Details?.Id}
                        asset={asset}
                        handleAssetDeleted={() => launchDeleteModal([asset])}
                        handleAssetEdited={() => handleEdit(asset)}
                    />
                ),
                baseObject: asset.Details
            }));
        }, [assets, launchDeleteModal, handleEdit]);

    const actionsBarOptions: ActionsBarOptions<AssetDetailsProps> = {
        showActions: true,
        actions: [
            {
                text: t("buttons:delete"),
                onClick: handleMultiDelete,
                buttonType: "outline",
                shouldDeselectOnClick: false,
                MobileIcon: TrashIcon
            }
        ]
    };
    const searchSortOptions: SearchSortOptions = {
        showSearchSort: true,
        location: "cloud-assets",
        showOrientation: variant === "all-assets"
    };

    const { GenericMultiSelectComponent } = useGenericMultiSelect<
        AssetDetailsResponse,
        AssetDetailsProps
    >({
        isMultiple: true,
        checkBoxLocation: "left",
        allowItemClick: false,
        items: assetListItemComponentItems,
        emptyStateCopy,
        actionsBarOptions,
        searchSortOptions,
        loading,
        showSelectedCountOnActions: true,
        scrollable: !allLoaded
    });

    return GenericMultiSelectComponent;
};
